import { create } from 'zustand';
import moment from 'moment';
import { authHeaders } from '../src/utils/authFetch';


interface StripePaymentMethod {
  id: string;
  type: string;
  card?: {
    brand: string;
    exp_month: number;
    exp_year: number;
    last4: string;
  };
}

interface StripeSubscriptionInfo {
  periodStart: string;
  periodEnd: string;
  subscription: any;
  status: string;
  default_payment_method?: any;
  plan?: {
    product?: any;
    interval: string;
  };
  closeDay?: number | null;
}

interface StripeFailedPayment {
  id: string;
  amount_due: number;
  status: string;
  created: number;
  customer_name: string;
  number: string;
  hosted_invoice_url: string;
  currency: string;
}

interface StripeInvoice {
    data: any[];
    has_more: boolean;
    object: string;
    url: string;
}


interface StripeStoreState {
  isLoading: boolean;
  error: string | null;
  // Products
  monthlyProducts: any[];
  yearlyProducts: any[];
  oneTimeProducts: any[];
  resellersPlans: any[];
  legacyPlans: any[];
  productsLoaded: boolean;
  productsLoading: boolean;
  stripeInfoCustomerId: string | null;
  // Subscription info
  activeSubscription: StripeSubscriptionInfo | null;
  pausedSubscription: any | null;
  incompleteSubscription: any | null;
  cancelledSubscription: any | null;
  // Payments and invoices
  failedPayments: StripeFailedPayment[];
  paymentMethods: StripePaymentMethod[];
  allInvoices: StripeInvoice | null;
  
  // Actions
  setIsLoading: (loading: boolean) => void;
  fetchStripeProducts: () => Promise<void>;
  fetchOrganizationStripeInfo: (customerId: string, organizationId: string) => Promise<void>;
}

const useStripeStore = create<StripeStoreState>((set, get) => ({
  isLoading: false,
  error: null,
  monthlyProducts: [],
  yearlyProducts: [],
  oneTimeProducts: [],
  resellersPlans: [],
  legacyPlans: [],
  productsLoaded: false,
  productsLoading: false,
  stripeInfoCustomerId: null,
  activeSubscription: null,
  pausedSubscription: null,
  incompleteSubscription: null,
  cancelledSubscription: null,
  failedPayments: [],
  paymentMethods: [],
  allInvoices: null,

  setIsLoading: (isLoading) => set({ isLoading }),

  fetchStripeProducts: async () => {
    if (get().productsLoaded || get().productsLoading) return;
    set({ productsLoading: true });
    try {
      const res = await fetch(`/api/get-products`, {
        method: 'GET',
      });

      if (res.ok) {
        const data = await res.json();
        set({ 
          monthlyProducts: data.monthly_plans,
          yearlyProducts: data.yearly_plans,
          oneTimeProducts: data.one_time_plans,
          resellersPlans: data.resellers_plans,
          // Retired plans: not offered any more, but customers are still on them.
          legacyPlans: data.legacy_plans || [],
          productsLoaded: true 
        });
      }
    } catch (error) {
      console.error('Error fetching stripe products:', error);
      set({ error: error.message });
    } finally {
      set({ productsLoading: false });
    }
  },

  fetchOrganizationStripeInfo: async (customerId: string, organizationId: string) => {
    if (!customerId) return;
    const { isLoading, stripeInfoCustomerId } = get();
    if (isLoading && stripeInfoCustomerId === customerId) return;
    const isDifferentCustomer = stripeInfoCustomerId !== customerId;
    set({
      isLoading: true,
      stripeInfoCustomerId: customerId,
      ...(isDifferentCustomer ? {
        activeSubscription: null,
        pausedSubscription: null,
        incompleteSubscription: null,
        cancelledSubscription: null,
        failedPayments: [],
      } : {}),
    });
    try {
      const res = await fetch(`/api/stripe-plan?customerId=${customerId}`, { headers: await authHeaders() });
      
      if (res.ok) {
        const data = await res.json();
        const { subscriptions, failedPayments, paymentMethods, allInvoices } = data;
        
        const pausedSubscription = subscriptions.find(sub => sub.status === 'paused');
        const cancelledSubscription = subscriptions.find(sub => sub.status === 'canceled');
        const incompleteSubscription = subscriptions.find(sub => sub.status === 'incomplete');
        const subscription = subscriptions.find(sub => sub.status === 'active' || sub.status === 'trialing');
        
        let activeSubscription = null;
        if (subscription) {
          let intervaloText = subscription.plan.interval === 'month' ? 'Mensual' : 'Anual';
          const closeDay = subscription.current_period_start
            ? new Date(Number(subscription.current_period_start) * 1000).getDate()
            : null;
          activeSubscription = {
            periodStart: subscription.current_period_start,
            periodEnd: subscription.current_period_end,
            closeDay,
            subscription: subscription,
            status: subscription.status,
            default_payment_method: subscription.default_payment_method,
            plan: {
              product: subscription.plan.product,
              name: subscription.plan.nickname || 'Standard',
              intervalo: intervaloText || 'Error al obtener el intervalo',
            }
          };

        }

        set({
          activeSubscription,
          pausedSubscription,
          incompleteSubscription,
          cancelledSubscription,
          failedPayments,
          paymentMethods,
          allInvoices,
          isLoading: false,
          error: null
        });
      } else {
        set({
          activeSubscription: null,
          pausedSubscription: null,
          incompleteSubscription: null,
          cancelledSubscription: null,
          isLoading: false,
          error: 'Failed to fetch stripe info',
        });
      }
    } catch (error) {
      console.error('Error fetching organization stripe info:', error);
      set({
        error: error.message,
        isLoading: false,
      });
    }
  },
}));

export default useStripeStore; 